import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { Button } from 'antd';

function getValueByDataIndex(record, dataIndex) {
  if (Array.isArray(dataIndex)) {
    let value = record;
    dataIndex.forEach((index) => {
      value = value?.[index];
    });
    return value;
  } else {
    return record?.[dataIndex];
  }
}

/**
 * 用來匯出 CSV 的按鈕
 *
 * @param {Object} param0
 * @param {Array<import('antd/lib/table').ColumnProps<any>>} param0.columns 欄位資訊
 * @param {Array} param0.data 資料
 * @param {String} param0.filename 檔名
 * @param {String} [param0.children] 按鈕文字
 * @param {{ [key: string]: (value, record, index)=> String }} [param0.overrideRender] 用來覆寫 render 以轉換為可匯出的文字
 * @returns
 */
export default function CSVDownloadButton({
  columns = [],
  data: dataSource = [],
  filename = 'table',
  children = '匯出',
  overrideRender = {},
}) {
  const csvButtonRef = React.createRef();
  const today = moment().format('YYYY-MM-DD');

  const getTextFromComponent = useCallback((component) => {
    return React.Children.map(component.props.children, (child) => {
      return typeof child === 'string' ? child : '';
    })?.join('');
  }, []);

  const columnsWithKeys = useMemo(() => {
    if (!columns || columns.length === 0) return [];
    
    // 處理分組欄位，將 children 展開，並組合上一層標題
    const flattenColumns = (cols, parentTitle = '') => {
      const result = [];
      cols.forEach((column, index) => {
        if (column.children && Array.isArray(column.children)) {
          // 如果有 children，遞歸處理子欄位，傳遞父級標題
          const parentTitleText = React.isValidElement(column.title)
            ? getTextFromComponent(column.title) || ''
            : column.title || '';
          result.push(...flattenColumns(column.children, parentTitleText));
        } else {
          // 普通欄位，組合標題
          let combinedTitle = column.title;
          if (React.isValidElement(combinedTitle)) {
            combinedTitle = getTextFromComponent(combinedTitle) || '';
          }
          
          // 如果有父級標題，組合標題
          if (parentTitle) {
            combinedTitle = `${parentTitle}_${combinedTitle}`;
          }
          
          result.push({
            ...column,
            title: combinedTitle,
            key: column.key ? column.key : `key-${index}`,
          });
        }
      });
      return result;
    };
    
    return flattenColumns(columns);
  }, [columns, getTextFromComponent]);

  const csvHeaders = useMemo(() => {
    return columnsWithKeys.map((column) => {
      // 處理 title 可能是 React 元素的情況
      let label = column.title;
      if (React.isValidElement(label)) {
        // 如果是 React 元素，嘗試提取文字內容
        label = getTextFromComponent(label) || column.key || '';
      }
      return {
        key: column.key,
        label: label,
      };
    });
  }, [columnsWithKeys, getTextFromComponent]);

  const csvData = useMemo(() => {
    if (!dataSource || dataSource.length === 0) return [];

    const csvData = [];
    dataSource.forEach((record) => {
      // 根據 csvHeaders 來產生 data
      const newData = {};
      columnsWithKeys.forEach((column) => {
        // 如果有 overrideRender，以 overrideRender 為優先
        const newKey = column.key;
        const newDataIndex = column.dataIndex?.toString();
        const newRender = overrideRender[newKey]
          ? overrideRender[newKey]
          : (newDataIndex && overrideRender[newDataIndex])
          ? overrideRender[newDataIndex]
          : null;
        // const value = record[header._column.dataIndex];
        const value = getValueByDataIndex(record, column.dataIndex);
        if (newRender) {
          const renderResult = newRender(value, record, 0);
          newData[newKey] = React.isValidElement(renderResult)
            ? getTextFromComponent(renderResult)
            : renderResult;
        } else {
          newData[newKey] = value || '';
        }
      });
      csvData.push(newData);
    });
    return csvData;
  }, [dataSource, columnsWithKeys, overrideRender, getTextFromComponent]);

  const handleDownloadClick = () => {
    csvButtonRef.current?.link?.click();
  };

  const isButtonDisabled = useMemo(() => {
    return !dataSource || !columns || dataSource.length < 1;
  }, [dataSource, columns]);

  return (
    <>
      {
        <Button type="primary" disabled={isButtonDisabled} onClick={handleDownloadClick}>
          {children}
        </Button>
      }
      <CSVLink
        ref={csvButtonRef}
        style={{ display: 'none' }}
        filename={`${filename}-${today}.csv`}
        data={csvData}
        headers={csvHeaders}
      />
    </>
  );
}